'use client'

import {
  IconDocumentPaperGray,
  IconEmojiAmazedColor,
  IconEmojiSadColor,
  IconEmojiSmileColor,
  IconThumbsUpFill,
} from '@macarongfactory/web-icons'
import { Divider, Label, Text } from '@macarongfactory/web-ui'

import FuelGauge from './FuelGauge'
import InfoSheet from './InfoSheet'
import InfoTooltip from './InfoTooltip'
import LockOverlay from './LockOverlay'
import { LOCK_AB_AF_SUB2 } from '../../_features/modoocar-lock-ab'
import { useLockAb } from '../../_features/modoocar-lock-ab/_hooks/useLockAb'
import {
  MODOOCAR_CODE,
  MODOOCAR_EVENT,
  MODOOCAR_MILEAGE_LOCK_ONELINK_URL,
  MODOOCAR_TREND_LOCK_ONELINK_URL,
  REAL_FUEL_STATUS_COLOR,
  calcOfficialRatio,
  getLockMileageStatus,
  getRealFuelStatus,
  getTrendDirection,
  getTrendLockMessage,
  shouldShowTrendSection,
  useModoocarAppLink,
  useModoocarImpression,
  useModoocarTracking,
} from '../_features/modoocar'

import type {
  TRealFuelStatusLevel,
  IModoocarDetail,
} from '../_features/modoocar'

interface IRealFuelSectionProps {
  detail: IModoocarDetail
}

const LABEL_ICON_SIZE_PX = 14
const STATUS_ICON_SIZE_PX = 20
const TREND_BAR_AREA_HEIGHT_PX = 110
const TREND_BASELINE_OFFSET = 5
const TREND_VALUE_LABEL_OFFSET_PX = 26
const TREND_BASELINE_OFFSET_PX =
  TREND_VALUE_LABEL_OFFSET_PX + TREND_BAR_AREA_HEIGHT_PX
const CARD_SHADOW = '0 2px 8px rgba(0, 0, 0, 0.08), 0 0 1px rgba(0, 0, 0, 0.06)'
const OFFICIAL_INFO_TEXT =
  '차량을 출시할때 제조사에서 공식적으로 측정한 연비 정보에요.'
const AVERAGE_INFO_SHEET_TITLE = '실연비 / 실전비'
const EFFICIENCY_RANK_LABEL_MAX = 100
const EMPTY_ICON_SIZE_PX = 56

const MILEAGE_LOCK_MESSAGE = '마이클 앱에서 확인 가능'
const MILEAGE_LOCK_BUTTON_TEXT = '앱에서 실연비 보기'
const MILEAGE_LOCK_LABEL = '실연비 잠금'
const TREND_LOCK_BUTTON_TEXT = '앱에서 보기'
const TREND_LOCK_LABEL = '추이 잠금'
const LOCKED_VALUE_TEXT = '??'
const PLACEHOLDER_TREND = [
  { month: '1월', value: 15.4 },
  { month: '2월', value: 15.4 },
  { month: '3월', value: 15.4 },
  { month: '4월', value: 15.4 },
  { month: '5월', value: 15.4 },
  { month: '6월', value: 16.2 },
]

const STATUS_ICON: Record<TRealFuelStatusLevel, typeof IconEmojiSmileColor> = {
  great: IconEmojiAmazedColor,
  good: IconEmojiSmileColor,
  low: IconEmojiSadColor,
}

const RealFuelSection = ({ detail }: IRealFuelSectionProps) => {
  const { upperModelName, realFuel, fuelType, efficiencyRank, fuelCost } =
    detail

  const isElectric = fuelType === '전기'
  const unit = isElectric ? 'km/kWh' : 'km/L'
  const realLabel = isElectric ? '실전비' : '실연비'
  const officialLabel = isElectric ? '공인전비' : '공인연비'
  const energyLabel = isElectric ? '전비' : '연비'

  const { sectionShow, itemShow, itemClick, track } = useModoocarTracking()
  const {
    data: { isLock },
  } = useLockAb()
  const sectionRef = useModoocarImpression<HTMLElement>(() =>
    sectionShow(MODOOCAR_CODE.REAL_MILEAGE, '실연비')
  )
  const hasRealFuelData =
    !!realFuel && realFuel.realFuelEconomy > 0 && realFuel.ownerCount > 0
  const isLockActive = isLock && hasRealFuelData
  const trendMonthCount = realFuel?.trendMonthCount ?? 0
  const hasTrendData = shouldShowTrendSection({
    monthCount: trendMonthCount,
    isLock: isLockActive,
  })
  const mileageLockStatus = hasRealFuelData
    ? getLockMileageStatus(
        realFuel.realFuelEconomy,
        realFuel.officialFuelEconomy
      )
    : null
  const trendDirection = hasRealFuelData
    ? getTrendDirection(realFuel.trend, trendMonthCount)
    : null
  // [주의]: 데이터가 부족한 잠금 화면은 흐림 뒤에 가짜 차트를 깝니다. 실제 차트가 아니어서 차트 노출 이벤트를 보내면 집계가 틀어집니다.
  const isPlaceholderTrend = isLockActive && trendDirection === 'insufficient'
  const chartRef = useModoocarImpression<HTMLDivElement>(
    () =>
      itemShow(
        MODOOCAR_CODE.REAL_MILEAGE,
        'chart',
        `최근 6개월 ${realLabel} 추이`
      ),
    { enabled: !isPlaceholderTrend }
  )
  const mileageLockRef = useModoocarImpression<HTMLDivElement>(
    () =>
      track(MODOOCAR_EVENT.SECTION_SHOW, {
        code: MODOOCAR_CODE.LOCK_MILEAGE,
        type: 'lock',
        label: MILEAGE_LOCK_LABEL,
        status: mileageLockStatus,
      }),
    { enabled: isLockActive }
  )
  const trendLockRef = useModoocarImpression<HTMLDivElement>(
    () =>
      track(MODOOCAR_EVENT.SECTION_SHOW, {
        code: MODOOCAR_CODE.LOCK_TREND,
        type: 'lock',
        label: TREND_LOCK_LABEL,
        status: trendDirection,
      }),
    { enabled: isLockActive && hasTrendData }
  )
  const openMileageLockLink = useModoocarAppLink({
    fallbackUrl: MODOOCAR_MILEAGE_LOCK_ONELINK_URL,
    afSub2: LOCK_AB_AF_SUB2.LOCK_MILEAGE,
  })
  const openTrendLockLink = useModoocarAppLink({
    fallbackUrl: MODOOCAR_TREND_LOCK_ONELINK_URL,
    afSub2: LOCK_AB_AF_SUB2.LOCK_TREND,
  })
  const handleMileageLockClick = () =>
    openMileageLockLink(() =>
      itemClick(
        MODOOCAR_CODE.LOCK_MILEAGE,
        'button',
        MILEAGE_LOCK_BUTTON_TEXT,
        {
          status: mileageLockStatus,
        }
      )
    )
  const handleTrendLockClick = () =>
    openTrendLockLink(() =>
      itemClick(MODOOCAR_CODE.LOCK_TREND, 'button', TREND_LOCK_BUTTON_TEXT, {
        status: trendDirection,
      })
    )

  const sectionTitle = (
    <h2 className='flex min-w-0 items-center'>
      <Text
        as='span'
        variant='title-1-semibold'
        color='text-foreground-neutral-strong'
        className='min-w-0 truncate'
      >
        {upperModelName}
      </Text>
      <Text
        as='span'
        variant='title-1-semibold'
        color='text-foreground-neutral-strong'
        className='shrink-0 whitespace-nowrap'
      >
        &nbsp;{realLabel}
      </Text>
    </h2>
  )

  if (!hasRealFuelData) {
    return (
      <section
        id='real-fuel'
        className='flex scroll-mt-[var(--mc-section-scroll-mt,97px)] flex-col gap-dimension-x4 px-dimension-x5 py-dimension-x8 lg:scroll-mt-[112px]'
      >
        {sectionTitle}
        <div className='flex flex-col items-center gap-dimension-x4 py-dimension-x10'>
          <IconDocumentPaperGray
            width={EMPTY_ICON_SIZE_PX}
            height={EMPTY_ICON_SIZE_PX}
          />
          <Text
            variant='title-1-semibold'
            color='text-foreground-neutral-strong'
          >
            아직 {energyLabel} 정보가 없는 차량이에요
          </Text>
        </div>
      </section>
    )
  }

  const hasOfficial = realFuel.officialFuelEconomy > 0
  const ratio = calcOfficialRatio(
    realFuel.realFuelEconomy,
    realFuel.officialFuelEconomy
  )
  const status = getRealFuelStatus(ratio)
  const StatusIcon = STATUS_ICON[status.level]
  const gaugeStatusColor = hasOfficial
    ? status.color
    : REAL_FUEL_STATUS_COLOR.good
  const showGoodLabel = efficiencyRank <= EFFICIENCY_RANK_LABEL_MAX

  const averageInfoLines = [
    `${officialLabel} 대비 80% 미만은 저조, 80~100% 양호, 100% 이상은 훌륭으로 표시됩니다.`,
    `${realLabel}는 마이클 오너의 실기록을 바탕으로 계산되었습니다.`,
    `유가는 오피넷 ${fuelCost.fuelPriceYearMonth} 유가 기준으로 계산되었습니다.`,
    `전기요금은 한국전력공사 ${fuelCost.fuelPriceYearMonth} 전기요금 기준으로 계산되었습니다.`,
    `실제 ${energyLabel}는 주행거리 · 유가 · 운전 습관에 따라 달라집니다.`,
  ]

  const chartTrend = isPlaceholderTrend ? PLACEHOLDER_TREND : realFuel.trend
  const hasTrend = chartTrend.length > 0
  const trendMax = hasTrend
    ? Math.max(...chartTrend.map(point => point.value))
    : 0
  const trendAvg = hasTrend
    ? Math.round(
        (chartTrend.reduce((sum, point) => sum + point.value, 0) /
          chartTrend.length) *
          100
      ) / 100
    : 0
  const lastTrendIndex = chartTrend.length - 1
  const showTrend = hasTrendData
  const trendBaseCandidate = Math.max(
    0,
    realFuel.realFuelEconomy - TREND_BASELINE_OFFSET
  )
  const trendBase = trendMax > trendBaseCandidate ? trendBaseCandidate : 0
  const trendSpan = trendMax - trendBase
  const trendHeightRatio = (value: number) =>
    trendSpan > 0
      ? Math.max(0, Math.min(1, (value - trendBase) / trendSpan))
      : 0

  const trendAvgLineTop =
    trendMax > 0
      ? TREND_BASELINE_OFFSET_PX -
        trendHeightRatio(trendAvg) * TREND_BAR_AREA_HEIGHT_PX
      : TREND_VALUE_LABEL_OFFSET_PX

  return (
    <section
      ref={sectionRef}
      id='real-fuel'
      className='flex scroll-mt-[var(--mc-section-scroll-mt,97px)] flex-col gap-dimension-x4 px-dimension-x5 py-dimension-x8 lg:scroll-mt-[112px]'
    >
      <div className='flex items-center gap-dimension-x2'>
        <h2 className='flex min-w-0 flex-1 items-center'>
          <Text
            as='span'
            variant='title-1-semibold'
            color='text-foreground-neutral-strong'
            className='min-w-0 truncate'
          >
            {upperModelName}
          </Text>
          <Text
            as='span'
            variant='title-1-semibold'
            color='text-foreground-neutral-strong'
            className='shrink-0 whitespace-nowrap'
          >
            &nbsp;{realLabel}
          </Text>
        </h2>
        {showGoodLabel ? (
          <div className='shrink-0'>
            <Label
              text={`${realLabel} 좋은 차`}
              type='weak'
              variant='brand'
              size='medium'
              leadingIcon={
                <IconThumbsUpFill
                  width={LABEL_ICON_SIZE_PX}
                  height={LABEL_ICON_SIZE_PX}
                />
              }
            />
          </div>
        ) : null}
      </div>

      <div
        className='overflow-hidden rounded-dimension-x3 border border-stroke-neutral-strong bg-background-surface-default'
        style={{ boxShadow: CARD_SHADOW }}
      >
        <div className='flex flex-col gap-dimension-x6 py-dimension-x5'>
          <FuelGauge
            realFuelEconomy={realFuel.realFuelEconomy}
            officialFuelEconomy={realFuel.officialFuelEconomy}
            unit={unit}
            realLabel={realLabel}
            statusIcon={
              hasOfficial ? (
                <StatusIcon
                  width={STATUS_ICON_SIZE_PX}
                  height={STATUS_ICON_SIZE_PX}
                />
              ) : undefined
            }
            statusLabel={hasOfficial ? status.label : undefined}
            statusColor={gaugeStatusColor}
            lockOverlay={
              isLockActive ? (
                <LockOverlay
                  impressionRef={mileageLockRef}
                  message={MILEAGE_LOCK_MESSAGE}
                  buttonText={MILEAGE_LOCK_BUTTON_TEXT}
                  buttonVariant='neutral'
                  onClick={handleMileageLockClick}
                />
              ) : undefined
            }
          />

          <div className='flex items-center'>
            {isLockActive ? (
              <div className='flex flex-1 flex-col items-center justify-end'>
                <div className='flex items-end'>
                  <span
                    className='text-[30px] font-semibold leading-[40px]'
                    style={{ color: gaugeStatusColor }}
                  >
                    {LOCKED_VALUE_TEXT}
                  </span>
                  <span className='inline-flex items-center py-dimension-x1'>
                    <Text
                      as='span'
                      variant='body-1-medium'
                      color={gaugeStatusColor}
                    >
                      {unit}
                    </Text>
                  </span>
                </div>
                <Text variant='body-1-semibold' color={gaugeStatusColor}>
                  {realLabel}
                </Text>
              </div>
            ) : (
              <div
                className={`flex flex-1 flex-col items-center ${
                  hasOfficial ? 'gap-dimension-x2' : ''
                }`}
              >
                {hasOfficial ? (
                  <div className='flex items-end gap-dimension-x0-5'>
                    <span
                      className='text-[30px] font-semibold leading-none'
                      style={{ color: status.color }}
                    >
                      {ratio}
                    </span>
                    <Text
                      as='span'
                      variant='body-1-medium'
                      color={status.color}
                      className='pb-[2px]'
                    >
                      %
                    </Text>
                  </div>
                ) : (
                  <Text
                    variant='heading-3-semibold'
                    color='text-foreground-neutral-alternative'
                  >
                    정보 없음
                  </Text>
                )}
                <Text
                  variant='body-1-semibold'
                  color={
                    hasOfficial
                      ? status.color
                      : 'text-foreground-neutral-alternative'
                  }
                >
                  {officialLabel} 대비
                </Text>
              </div>
            )}

            <div
              className='h-dimension-x10 w-px bg-background-neutral-muted'
              aria-hidden
            />

            <div
              className={`flex flex-1 flex-col items-center ${
                hasOfficial ? 'gap-dimension-x2' : ''
              }`}
            >
              {hasOfficial ? (
                <div className='flex items-end gap-dimension-x0-5'>
                  <span className='text-[30px] font-semibold leading-none text-foreground-neutral-alternative'>
                    {realFuel.officialFuelEconomy}
                  </span>
                  <Text
                    as='span'
                    variant='body-1-medium'
                    color='text-foreground-neutral-alternative'
                    className='pb-[2px]'
                  >
                    {unit}
                  </Text>
                </div>
              ) : (
                <Text
                  variant='heading-3-semibold'
                  color='text-foreground-neutral-alternative'
                >
                  정보 없음
                </Text>
              )}
              <div className='flex items-center gap-dimension-x0-5'>
                <Text
                  variant='body-1-semibold'
                  color='text-foreground-neutral-alternative'
                >
                  {officialLabel}
                </Text>
                <InfoTooltip
                  text={OFFICIAL_INFO_TEXT}
                  ariaLabel={`${officialLabel} 설명`}
                  align='right'
                />
              </div>
            </div>
          </div>
        </div>

        <div className='flex items-center justify-center border-t border-stroke-neutral-strong bg-background-neutral-weak px-dimension-x4 py-dimension-x5'>
          <Text
            variant='title-1-semibold'
            color='text-foreground-neutral-normal'
            className='break-keep text-center'
          >
            {isLockActive && hasOfficial ? (
              <>
                {officialLabel} 대비{' '}
                <Text
                  as='span'
                  variant='title-1-semibold'
                  color={gaugeStatusColor}
                >
                  {LOCKED_VALUE_TEXT}%
                </Text>{' '}
              </>
            ) : (
              <>
                {realLabel}{' '}
                <Text
                  as='span'
                  variant='title-1-semibold'
                  color={gaugeStatusColor}
                >
                  {isLockActive ? LOCKED_VALUE_TEXT : realFuel.realFuelEconomy}
                  {unit}
                </Text>{' '}
              </>
            )}
            로
            {hasOfficial ? (
              <>
                <br />
                {status.phrase(officialLabel)}{' '}
              </>
            ) : (
              ' '
            )}
            주행되고 있어요
          </Text>
        </div>
      </div>

      {hasTrendData ? (
        <div
          className={`flex flex-col gap-dimension-x5 ${
            showTrend ? 'pt-dimension-x6' : ''
          }`}
        >
          {showTrend ? (
            <Text
              variant='title-1-semibold'
              color='text-foreground-neutral-strong'
              className='text-center'
            >
              최근 6개월 {realLabel} 추이
            </Text>
          ) : null}

          <div className='relative flex flex-col gap-dimension-x5'>
            {showTrend ? (
              <div
                ref={chartRef}
                className={`relative flex pr-dimension-x8 ${
                  isLockActive ? 'blur-[3.5px]' : ''
                }`}
                aria-hidden={isLockActive ? true : undefined}
              >
                {trendMax > 0 ? (
                  <div
                    aria-hidden
                    className='pointer-events-none absolute inset-x-0 flex -translate-y-1/2 items-center gap-dimension-x2'
                    style={{ top: `${trendAvgLineTop}px` }}
                  >
                    <div className='h-px flex-1 bg-stroke-neutral-normal' />
                    <Text
                      variant='caption-1-medium'
                      color='text-foreground-neutral-assistive'
                    >
                      {trendAvg}
                    </Text>
                  </div>
                ) : null}

                <div
                  aria-hidden
                  className='pointer-events-none absolute inset-x-0 pr-dimension-x8'
                  style={{ top: `${TREND_BASELINE_OFFSET_PX}px` }}
                >
                  <Divider.Horizontal />
                </div>

                {chartTrend.map((point, index) => {
                  const isCurrent = index === lastTrendIndex
                  return (
                    <div
                      key={point.month}
                      className='flex flex-1 flex-col items-center'
                      aria-label={`${point.month} ${point.value}${unit}`}
                    >
                      <span
                        className={`text-center text-[17px] font-semibold leading-[25.5px] ${
                          isCurrent
                            ? 'text-foreground-brand-strong'
                            : 'text-light-mycle-300'
                        }`}
                      >
                        {point.value}
                      </span>
                      <div
                        className='flex items-end'
                        style={{ height: `${TREND_BAR_AREA_HEIGHT_PX}px` }}
                      >
                        <div
                          className={`w-dimension-x5 rounded-t-dimension-x1 ${
                            isCurrent
                              ? 'bg-background-brand-strong'
                              : 'bg-background-brand-weak'
                          }`}
                          style={{
                            height:
                              trendMax > 0
                                ? `${trendHeightRatio(point.value) * TREND_BAR_AREA_HEIGHT_PX}px`
                                : '0px',
                          }}
                          aria-hidden
                        />
                      </div>
                      <Text
                        variant='caption-1-medium'
                        color='text-foreground-neutral-assistive'
                        className='mt-dimension-x2'
                      >
                        {isCurrent ? '최근' : point.month}
                      </Text>
                    </div>
                  )
                })}
              </div>
            ) : null}

            <div
              className={`flex items-center justify-between rounded-dimension-x4 bg-background-neutral-weak px-dimension-x4 py-dimension-x4 ${
                isLockActive ? 'blur-[5px]' : ''
              }`}
              aria-hidden={isLockActive ? true : undefined}
            >
              <div className='flex items-center gap-dimension-x1'>
                <Text
                  variant='title-2-semibold'
                  color='text-foreground-neutral-strong'
                >
                  평균 {realLabel}
                </Text>
                <InfoSheet
                  title={AVERAGE_INFO_SHEET_TITLE}
                  lines={averageInfoLines}
                  ariaLabel={`평균 ${realLabel} 설명`}
                />
              </div>
              <Text
                variant='title-1-semibold'
                color='text-foreground-neutral-strong'
              >
                {trendAvg}
                {unit}
              </Text>
            </div>

            {isLockActive && trendDirection ? (
              <LockOverlay
                impressionRef={trendLockRef}
                message={getTrendLockMessage(trendDirection, realLabel)}
                buttonText={TREND_LOCK_BUTTON_TEXT}
                buttonVariant='brand'
                onClick={handleTrendLockClick}
              />
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  )
}

export default RealFuelSection
